import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { type ClassName, tw, twx } from '@ctw/shared/utils/tailwind';

// The options a numbered-pagination table offers. Includes both surfaces' current defaults (10
// and 25) so adopting this selector never silently changes a default for a user who never touches it.
export const PAGE_SIZE_OPTIONS: ReadonlyArray<number> = [10, 25, 50, 100, 250, 500];

// Resolves a persisted page-size string to a valid page size, falling back to `fallbackPageSize`
// when the stored value is missing, malformed, or not one of `PAGE_SIZE_OPTIONS`.
export const parsePageSizePreference = (storedValue: string, fallbackPageSize: number): number => {
  const parsed = Number(storedValue);
  return PAGE_SIZE_OPTIONS.includes(parsed) ? parsed : fallbackPageSize;
};

export interface PageSizeSelectProps {
  id?: string;
  testId?: string;
  className?: ClassName;
  // Names what the control changes (e.g. "Results per page") so the label stands on its own
  // rather than relying on surrounding text.
  label: string;
  value: number;
  onChange: (pageSize: number) => void;
}

export const PageSizeSelect = ({
  id,
  testId,
  className,
  label,
  value,
  onChange,
}: PageSizeSelectProps) => {
  const { trackInteraction } = useTelemetry();

  return (
    <label
      id={id}
      data-testid={testId}
      className={twx('flex items-center gap-2 text-content-subtle text-sm', className)}
    >
      {label}
      <select
        value={value}
        onChange={(event) => {
          const nextPageSize = Number(event.target.value);
          trackInteraction('pagination_page_size_change', {
            paginatedFromPageSize: value,
            paginatedToPageSize: nextPageSize,
          });
          onChange(nextPageSize);
        }}
        className={tw`rounded border border-divider-main bg-white px-2 py-1 font-medium text-content-main text-sm`}
      >
        {PAGE_SIZE_OPTIONS.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </label>
  );
};
